<template>
    <section class="member-page">
        <!-- Page title -->
        <v-container class="nav">
            <v-card-title>
                <v-row align="center">
                    <v-col
                        cols="2"
                        sm="2"
                        lg="1"
                        class="pr-0"
                        align="right"
                    >
                        <v-avatar class="page-icon teal mr-2 elevation-5">
                            <v-icon large class="white--text">
                                {{ mdiInformationVariant }}
                            </v-icon>
                        </v-avatar>
                    </v-col>
                    <v-col
                        cols="10"
                        sm="10"
                        lg="11"
                        class="pl-0"
                    >
                        <h2 style="word-break: normal;">
                            Member Information
                        </h2>
                    </v-col>
                </v-row>
            </v-card-title>
            <v-divider />
        </v-container>

        <v-container class="member-main">
            <v-card class="user-card elevation-5">
                <!-- ===== LOADER (skeleton that mirrors the real layout) ===== -->
                <div
                    v-if="loading"
                    role="status"
                    aria-live="polite"
                    aria-busy="true"
                >
                    <span class="sr-only">Loading member information…</span>

                    <div class="hmi-card-wrap">
                        <div class="hmi-card-head">
                            <div class="sk sk-heading"></div>
                        </div>
                        <div class="sk sk-card"></div>
                    </div>

                    <div class="member-summary">
                        <div class="sk sk-avatar"></div>
                        <div class="member-summary-text">
                            <div class="sk sk-name"></div>
                            <div class="sk sk-type"></div>
                        </div>
                        <div class="sk sk-chip"></div>
                    </div>

                    <v-divider />

                    <div class="details">
                        <section v-for="n in 2" :key="n" class="details-group">
                            <div class="sk sk-title"></div>
                            <div v-for="r in 5" :key="r" class="details-row">
                                <div class="sk sk-label"></div>
                                <div class="sk sk-value"></div>
                            </div>
                        </section>
                    </div>
                </div>

                <!-- ===== CONTENT ===== -->
                <template v-else>
                    <!-- ===== MEMBER CARD ===== -->
                    <div class="hmi-card-wrap">
                        <HmiCard
                            :name="card.name"
                            :company-name="card.companyName"
                            :member-number="card.memberNumber"
                            :card-no="card.cardNo"
                            :dob="card.dob"
                            :sex="card.sex"
                            :dental="card.dental"
                            :status="isActive"
                        />
                    </div>

                    <!-- ===== SUMMARY ===== -->
                    <div class="member-summary">
                        <v-avatar class="green accent-3 elevation-5" size="48">
                            <v-icon large class="white--text">
                                {{ mdiAccount }}
                            </v-icon>
                        </v-avatar>
                        <div class="member-summary-text">
                            <div class="member-summary-name">
                                {{ card.name }}
                            </div>
                            <div class="member-summary-type">
                                {{ memberType }}
                            </div>
                        </div>
                    </div>

                    <v-divider />

                    <!-- ===== DETAILS ===== -->
                    <MembershipInfo :detail-groups="detailGroups" />
                    <MsuAccess
                        :hospital-code="msuAccess.HOSP_MSU || ''"
                        :hospital-desc="msuAccess.HOSP_MSU_DESC || ''"
                        :clinic-code="msuAccess.CLIN_MSU || ''"
                        :clinic-desc="msuAccess.CLIN_MSU_DESC || ''"
                        :status="statusMapper(msuAccess.MEMB_STATUS)"
                    />
                </template>
            </v-card>
        </v-container>
    </section>
</template>

<script>
import { moment } from '../../utils/date-shim'
import { mdiAccount, mdiInformationVariant } from '@mdi/js'
import { mapGetters } from 'vuex'
import HmiCard from './MembInfoComp/HmiCard.vue'
import MsuAccess from './MembInfoComp/MsuAccess.vue'
import MembershipInfo from './MembInfoComp/MembershipInfo.vue'

const MANILA = '+0800'
const fmtDate = (d) => (d ? moment(d).utcOffset(MANILA).format('DD MMMM YYYY') : '')

export default {
    components: { HmiCard, MsuAccess, MembershipInfo },
    data: () => ({
        mdiInformationVariant,
        mdiAccount
    }),
    computed: {
        ...mapGetters(['getUserInfo']),

        // true until the member data has arrived
        loading() {
            return !(this.getUserInfo && this.getUserInfo.USER_DATA)
        },
        user() {
            return (this.getUserInfo && this.getUserInfo.USER_DATA) || {}
        },
        status() {
            return (this.getUserInfo && this.getUserInfo.USER_DATA && this.getUserInfo.USER_DATA.MEMB_STATUS) || ''
        },
        msuAccess() {
            const u = this.user
            return {
                HOSP_MSU: u.HOSP_MSU || '',
                HOSP_MSU_DESC: u.HOSP_MSU_DESC || '',
                CLIN_MSU: u.CLIN_MSU || '',
                CLIN_MSU_DESC: u.CLIN_MSU_DESC || '',
                MEMB_STATUS: u.MEMB_STATUS || ''
            }
        },
        isActive() {
            return ['ACTIVE', 'WITH EXTENSION'].includes(this.status)
        },
        isExpired() {
            if (!this.user.EXPI_DATE) return false
            const exp = moment(this.user.EXPI_DATE).utcOffset(MANILA).startOf('day')
            const today = moment().utcOffset(MANILA).startOf('day')
            return exp.isBefore(today)
        },
        memberType() {
            return this.user.MEMB_TYPE == null ? 'Principal' : 'Dependent'
        },

        // Values printed on the card.
        // NOTE: CARD_NO and DENTAL are guesses - rename to your API's real fields.
        card() {
            const u = this.user
            return {
                name: [u.NAME, u.MIDI_M].filter(Boolean).join(' '),
                companyName: u.COMP_NAME || '',
                memberNumber: u.MEMB_N || '',
                cardNo: u.CARD_NO || '',
                dob: u.F_BIRTHDATE || '',
                sex: u.SEX || '',
                dental: u.DENTAL || ''
            }
        },

        // The details list under the card, rendered with v-for (no child components).
        detailGroups() {
            const u = this.user
            return [
                {
                    title: 'Membership',
                    items: [
                        { label: 'Member Status', value: this.status, tone: this.isActive ? 'ok' : 'bad' },
                        { label: 'Member Type', value: this.memberType },
                        { label: 'Room Type', value: u.ROOM_TYPE }
                    ]
                },
                {
                    title: 'Validity',
                    items: [
                        { label: 'Effectivity Date', value: fmtDate(u.EFF_DATE) },
                        { label: 'Expiration Date', value: fmtDate(u.EXPI_DATE), tone: this.isExpired ? 'bad' : 'ok' }
                    ]
                }
            ]
        }
    },
    methods: {
        statusMapper(status) {
            return ['ACTIVE', 'WITH EXTENSION'].includes(status)
        }
    }
}
</script>

<style scoped>
/* ================= Page ================= */
.member-page {
  width: 100%;
  min-height: 100%;
  background-image: url("../../assets/dashboardBG.webp");
  background-position: center;
  background-size: cover;
}
.member-main {
  display: flex;
  justify-content: center;
  padding-top: 24px;
  padding-bottom: 40px;
}
.user-card {
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  overflow: hidden;
}
.v-btn::before {
  background-color: transparent;
}

/* ================= Member card ================= */
.hmi-card-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 28px 16px 8px;
}
.hmi-card-head {
  width: 100%;
  max-width: 560px;
  margin-bottom: 14px;
}
.hmi-card-head h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
}

/* ================= Summary row ================= */
.member-summary {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 28px 16px;
}
.member-summary-text {
  flex: 1 1 auto;
  min-width: 0;
}
.member-summary-name {
  font-size: 18px;
  font-weight: 700;
  line-height: 1.25;
  overflow-wrap: anywhere;
}
.member-summary-type {
  font-size: 13px;
  color: rgba(0, 0, 0, .6);
}
.member-summary-status {
  flex: 0 0 auto;
  font-weight: 700;
}
/* ================= Loader (skeleton) ================= */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}
.sk {
  background: linear-gradient(90deg, #e3e8eb 25%, #f1f4f6 37%, #e3e8eb 63%);
  background-size: 400% 100%;
  animation: sk-shimmer 1.4s ease infinite;
  border-radius: 6px;
}
@keyframes sk-shimmer {
  0%   { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}
.sk-heading { width: 180px; height: 20px; }
.sk-card {
  width: 100%;
  max-width: 560px;
  aspect-ratio: 85.6 / 54;
  border-radius: 18px;
}
.sk-avatar { flex: 0 0 auto; width: 48px; height: 48px; border-radius: 50%; }
.sk-name { width: 55%; max-width: 260px; height: 18px; margin-bottom: 8px; }
.sk-type { width: 25%; max-width: 110px; height: 12px; }
.sk-chip { flex: 0 0 auto; width: 64px; height: 24px; border-radius: 12px; }
.sk-title { width: 90px; height: 12px; margin-bottom: 8px; }
.sk-label { width: 70%; height: 12px; }
.sk-value { width: min(220px, 100%); height: 15px; }
@media (max-width: 599px) {
  .sk-label { width: 90px; }
  .sk-value { width: 120px; }
}
@media (prefers-reduced-motion: reduce) {
  .sk { animation: none; }
}

/* ================= Responsive ================= */
/* Phones */
@media (max-width: 599px) {
  .nav { margin-top: 60px; }
  .nav h2 { font-size: 25px; }

  .member-main { padding-left: 8px; padding-right: 8px; }
  .hmi-card-wrap { padding: 20px 10px 4px; }
  .member-summary { padding: 16px 16px 12px; }
  .details { padding: 12px 16px 24px; gap: 4px; }

  /* label left, value right, one line each */
  .details-row { grid-template-columns: 1fr auto; }
  .details-row dt { font-size: 12px; }
  .details-row dd { font-size: 14px; text-align: right; max-width: 60vw; }
}
/* Very small phones */
@media (max-width: 390px) {
  .page-icon { display: none; }
}
/* Tablets */
@media (min-width: 600px) and (max-width: 1264px) {
  .nav { margin-top: 60px; }
  .nav h2 { font-size: 25px; }
  .details { gap: 8px 32px; }
  .details-row dt { font-size: 12px; }
  .details-row dd { font-size: 14px; }
}
</style>
